import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://spinebase.app/api';
function getHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

function CohortTag({ label, onRemove }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:'6px', background:'rgba(74,158,255,0.12)', color:'#4a9eff', border:'1px solid rgba(74,158,255,0.3)', borderRadius:'6px', padding:'5px 10px', fontSize:'12px', fontWeight:'600' }}>
      {label}
      {onRemove && <button onClick={onRemove} style={{ background:'none', border:'none', color:'#4a9eff', cursor:'pointer', fontSize:'16px', lineHeight:1, padding:0, opacity:0.7 }}>×</button>}
    </span>
  );
}

function CohortInput({ existingCohorts, userCohorts, onAdd }) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = existingCohorts.filter(c => !userCohorts.includes(c) && c.toLowerCase().includes(value.toLowerCase())).slice(0,6);
  function doAdd(cohort) {
    const clean = (cohort || value).trim().toUpperCase();
    if (!clean) return;
    onAdd(clean); setValue(''); setOpen(false);
  }
  return (
    <div style={{ position:'relative' }}>
      <div style={{ display:'flex', gap:'8px' }}>
        <input
          style={{ flex:1, background:'#1e293b', border:'1px solid #334155', borderRadius:'8px', color:'#e2e8f0', padding:'9px 12px', fontSize:'14px', outline:'none' }}
          placeholder="Add cohort (e.g. SRC, SFCR…)"
          value={value}
          onChange={e => { setValue(e.target.value); setOpen(true); }}
          onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();doAdd();} if(e.key==='Escape'){setValue('');setOpen(false);} }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        <button onClick={() => doAdd()} disabled={!value.trim()} style={{ background:'#4a9eff', border:'none', color:'#fff', padding:'9px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>
          + Add
        </button>
      </div>
      {open && filtered.length > 0 && value && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#1e293b', border:'1px solid #334155', borderRadius:'8px', marginTop:'4px', zIndex:100, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
          {filtered.map(c => (
            <div key={c} onMouseDown={() => doAdd(c)} style={{ padding:'10px 14px', fontSize:'13px', cursor:'pointer', color:'#cbd5e1', borderBottom:'1px solid #273449' }}>{c}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditDrawer({ user, onClose, existingCohorts, onCohortsChanged }) {
  const [cohorts, setCohorts] = useState(user.cohorts || []);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { setCohorts(user.cohorts || []); setMsg(null); }, [user.id]);

  async function addCohort(cohort) {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/admin/users/${user.id}/cohorts`, { method:'POST', headers:getHeaders(), body:JSON.stringify({ cohort }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCohorts(data.user.cohorts);
      onCohortsChanged(user.id, data.user.cohorts);
      setMsg({ type:'ok', text:`Added to ${cohort}` });
    } catch(e) { setMsg({ type:'err', text:e.message }); }
    finally { setSaving(false); }
  }

  async function removeCohort(cohort) {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/admin/users/${user.id}/cohorts/${cohort}`, { method:'DELETE', headers:getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCohorts(data.user.cohorts);
      onCohortsChanged(user.id, data.user.cohorts);
      setMsg({ type:'ok', text:`Removed from ${cohort}` });
    } catch(e) { setMsg({ type:'err', text:e.message }); }
    finally { setSaving(false); }
  }

  return (
    <div onClick={e => e.target===e.currentTarget && onClose()} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', zIndex:1000, display:'flex', justifyContent:'flex-end' }}>
      <div style={{ width:'420px', maxWidth:'95vw', background:'#0f172a', borderLeft:'1px solid #1e293b', height:'100vh', overflowY:'auto', boxShadow:'-20px 0 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'28px 24px 20px', borderBottom:'1px solid #1e293b' }}>
          <div>
            <div style={{ fontSize:'18px', fontWeight:'700', color:'#f1f5f9' }}>{user.first_name} {user.last_name}</div>
            <div style={{ fontSize:'13px', color:'#64748b', marginTop:'4px' }}>{user.email}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#475569', fontSize:'18px', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ display:'flex', gap:'16px', padding:'16px 24px', borderBottom:'1px solid #1e293b', flexWrap:'wrap' }}>
          {[['Role', user.role, '#4a9eff'], ['Patients', user.patient_count||0, '#34d399'], ['Joined', new Date(user.created_at).toLocaleDateString('en-GB',{month:'short',year:'numeric'}), '#a78bfa']].map(([label,val,color]) => (
            <div key={label} style={{ display:'flex', alignItems:'center', gap:'6px' }}>
              <span style={{ width:'7px', height:'7px', borderRadius:'50%', background:color, flexShrink:0 }}/>
              <span style={{ fontSize:'11px', color:'#475569', textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</span>
              <span style={{ fontSize:'13px', fontWeight:'600', color:'#cbd5e1' }}>{val}</span>
            </div>
          ))}
        </div>
        <div style={{ padding:'24px' }}>
          <div style={{ fontSize:'13px', fontWeight:'700', letterSpacing:'0.05em', textTransform:'uppercase', color:'#94a3b8', marginBottom:'6px' }}>Research Cohorts</div>
          <div style={{ fontSize:'13px', color:'#475569', marginBottom:'16px', lineHeight:'1.5' }}>Cohort members get free access to their cohort's anonymized data.</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', marginBottom:'16px', minHeight:'32px' }}>
            {cohorts.length === 0 && <span style={{ fontSize:'13px', color:'#334155', fontStyle:'italic' }}>No cohorts assigned</span>}
            {cohorts.map(c => <CohortTag key={c} label={c} onRemove={saving ? null : () => removeCohort(c)} />)}
          </div>
          <CohortInput existingCohorts={existingCohorts} userCohorts={cohorts} onAdd={addCohort} />
          {msg && (
            <div style={{ marginTop:'12px', fontSize:'13px', padding:'8px 12px', borderRadius:'6px', ...(msg.type==='ok' ? { background:'rgba(52,211,153,0.1)', color:'#34d399', border:'1px solid rgba(52,211,153,0.2)' } : { background:'rgba(239,68,68,0.1)', color:'#f87171', border:'1px solid rgba(239,68,68,0.2)' }) }}>{msg.text}</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCohort, setFilterCohort] = useState('');
  const [existingCohorts, setExistingCohorts] = useState([]);
  const [selected, setSelected] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCohort) params.append('cohort', filterCohort);
      const res = await fetch(`${API}/admin/users?${params}`, { headers: getHeaders() });
      const data = await res.json();
      setUsers(data.users || []);
    } catch(e) {} finally { setLoading(false); }
  }, [search, filterCohort]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => {
    fetch(`${API}/admin/cohorts`, { headers: getHeaders() })
      .then(r => r.json()).then(d => setExistingCohorts(d.cohorts || [])).catch(()=>{});
  }, []);

  function handleCohortsChanged(userId, newCohorts) {
    setUsers(prev => prev.map(u => u.id===userId ? {...u, cohorts:newCohorts} : u));
    fetch(`${API}/admin/cohorts`, { headers: getHeaders() }).then(r=>r.json()).then(d=>setExistingCohorts(d.cohorts||[])).catch(()=>{});
    if (selected?.id === userId) setSelected(prev => ({...prev, cohorts: newCohorts}));
  }

  const inputStyle = { background:'#1e293b', border:'1px solid #334155', borderRadius:'8px', color:'#e2e8f0', padding:'9px 14px', fontSize:'14px', outline:'none' };

  return (
    <div style={{ minHeight:'100vh', backgroundColor:'#0d0f14', color:'#e2e8f0', fontFamily:"'DM Sans','Inter',sans-serif", padding:'32px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'28px' }}>
        <div>
          <h1 style={{ fontSize:'24px', fontWeight:'700', color:'#f1f5f9', margin:0, letterSpacing:'-0.5px' }}>Contributors</h1>
          <p style={{ fontSize:'14px', color:'#64748b', margin:'4px 0 0' }}>Manage surgeons, cohort assignments, and data access</p>
        </div>
        <span style={{ fontSize:'12px', background:'#1e293b', color:'#94a3b8', padding:'4px 10px', borderRadius:'20px', border:'1px solid #334155' }}>{users.length} shown</span>
      </div>
      <div style={{ display:'flex', gap:'12px', marginBottom:'20px' }}>
        <input style={{ ...inputStyle, flex:1, minWidth:'220px' }} placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, cursor:'pointer' }} value={filterCohort} onChange={e => setFilterCohort(e.target.value)}>
          <option value="">All cohorts</option>
          {existingCohorts.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {loading ? (
        <div style={{ padding:'40px', textAlign:'center', color:'#475569' }}>Loading…</div>
      ) : (
        <div style={{ borderRadius:'12px', border:'1px solid #1e293b', overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'14px' }}>
            <thead>
              <tr>{['Surgeon','Email','Cohorts','Patients','Joined',''].map(h => (
                <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:'11px', fontWeight:'600', letterSpacing:'0.08em', textTransform:'uppercase', color:'#475569', background:'#111827', borderBottom:'1px solid #1e293b' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan={6} style={{ padding:'40px', textAlign:'center', color:'#334155' }}>No contributors found</td></tr>}
              {users.map(user => (
                <tr key={user.id} onClick={() => setSelected(user)} style={{ borderBottom:'1px solid #1a2234', cursor:'pointer' }}>
                  <td style={{ padding:'14px 16px' }}>
                    <div style={{ fontWeight:'500', color:'#f1f5f9' }}>{user.first_name} {user.last_name}</div>
                    <div style={{ fontSize:'12px', color:'#475569', marginTop:'2px', textTransform:'capitalize' }}>{user.role}</div>
                  </td>
                  <td style={{ padding:'14px 16px', color:'#94a3b8', fontSize:'13px' }}>{user.email}</td>
                  <td style={{ padding:'14px 16px' }}>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'5px' }}>
                      {(user.cohorts||[]).length===0 && <span style={{ color:'#334155' }}>—</span>}
                      {(user.cohorts||[]).map(c => <span key={c} style={{ fontSize:'11px', fontWeight:'600', background:'rgba(74,158,255,0.12)', color:'#4a9eff', border:'1px solid rgba(74,158,255,0.25)', borderRadius:'4px', padding:'2px 7px' }}>{c}</span>)}
                    </div>
                  </td>
                  <td style={{ padding:'14px 16px', color:'#34d399', fontWeight:'600' }}>{user.patient_count||0}</td>
                  <td style={{ padding:'14px 16px', color:'#64748b', fontSize:'13px' }}>{new Date(user.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'})}</td>
                  <td style={{ padding:'14px 16px' }}>
                    <button onClick={e=>{e.stopPropagation();setSelected(user);}} style={{ background:'transparent', border:'1px solid #334155', color:'#94a3b8', padding:'5px 12px', borderRadius:'6px', fontSize:'12px', cursor:'pointer' }}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selected && <EditDrawer user={selected} onClose={() => setSelected(null)} existingCohorts={existingCohorts} onCohortsChanged={handleCohortsChanged} />}
    </div>
  );
}
